import { ERROR_MESSAGES, type ApiError, type ApiErrorCode, type FieldIssue } from '@amai/contracts';

/**
 * Erro vindo da API, já traduzido.
 *
 * Regra permanente: o operador nunca vê stack trace nem jargão. Toda mensagem
 * exibida sai daqui ou do `message` que a própria API mandou (que já vem em
 * pt-BR e escrito para quem opera).
 */
export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly issues: FieldIssue[];
  readonly requestId?: string;

  constructor(status: number, body: ApiError['error']) {
    super(body.message || ERROR_MESSAGES[body.code]);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body.code;
    this.issues = body.issues ?? [];
    this.requestId = body.requestId;
  }

  /** true quando faz sentido oferecer "tentar de novo". */
  get isRetryable(): boolean {
    return this.status >= 500 || this.code === 'RATE_LIMITED';
  }
}

/** Falha de rede: a requisição nem chegou ao servidor. */
export class NetworkError extends Error {
  constructor() {
    super('Não foi possível falar com o servidor. Verifique sua conexão.');
    this.name = 'NetworkError';
  }
}

/** Mensagem pronta para a tela, seja qual for a origem do erro. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError || error instanceof NetworkError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return ERROR_MESSAGES.INTERNAL;
}

/** Erros de formulário no formato que o react-hook-form entende. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiRequestError)) return {};
  return Object.fromEntries(error.issues.map((issue) => [issue.path, issue.message]));
}
